import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  UpdateIcon,
} from '@radix-ui/react-icons';
import { Box, Flex, Grid, IconButton } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTooltip } from 'components/common/tooltip';
import { MachineProjectorToggleButton } from 'components/machine/buttons/projector-toggle';
import env from 'config';
import { MachineContext } from 'contexts/machine.context';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext } from 'react';

const COMPONENT_NAME = 'ProjectorSetupDialog';
const DELTA = 0.005;

const ENABLE_PROJECTOR_RESCALING = false;

interface IProps {
  // replaces UI w/ callout while user is not active
  isActive: boolean;
  onClose: () => void;
}

export const ProjectorSetupDialog = (props: IProps) => {
  const { adjustKeystone } = useContext(MachineContext);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.MIN}
        title="common.projector-setup"
        content={
          props.isActive ? (
            <Flex direction="column" gap={RADIX.FLEX.GAP.LG} justify="between">
              {ENABLE_PROJECTOR_RESCALING && (
                <Flex justify="center">
                  <Grid columns="3" gap={RADIX.FLEX.GAP.MD}>
                    {/* row 1 */}
                    <Box gridColumn="2">
                      <IconButton
                        size={RADIX.BUTTON.SIZE.LG}
                        color={RADIX.COLOR.NEUTRAL}
                        onClick={() =>
                          adjustKeystone(COMPONENT_NAME, {
                            delta_v: DELTA,
                          })
                        }
                      >
                        <ArrowUpIcon />
                      </IconButton>
                    </Box>
                    {/* row 2 */}
                    <Box gridColumn="1">
                      <IconButton
                        size={RADIX.BUTTON.SIZE.LG}
                        color={RADIX.COLOR.NEUTRAL}
                        onClick={() =>
                          adjustKeystone(COMPONENT_NAME, {
                            delta_h: -DELTA,
                          })
                        }
                      >
                        <ArrowLeftIcon />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton
                        size={RADIX.BUTTON.SIZE.LG}
                        color={RADIX.COLOR.NEUTRAL}
                        onClick={() =>
                          adjustKeystone(COMPONENT_NAME, {
                            reset_v: true,
                            reset_h: true,
                          })
                        }
                      >
                        <UpdateIcon />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton
                        size={RADIX.BUTTON.SIZE.LG}
                        color={RADIX.COLOR.NEUTRAL}
                        onClick={() =>
                          adjustKeystone(COMPONENT_NAME, {
                            delta_h: DELTA,
                          })
                        }
                      >
                        <ArrowRightIcon />
                      </IconButton>
                    </Box>
                    {/* row 3 */}
                    <Box gridColumn="2">
                      <IconButton
                        size={RADIX.BUTTON.SIZE.LG}
                        color={RADIX.COLOR.NEUTRAL}
                        onClick={() =>
                          adjustKeystone(COMPONENT_NAME, {
                            delta_v: -DELTA,
                          })
                        }
                      >
                        <ArrowDownIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Flex>
              )}

              <Flex direction="column" justify="center" gap={RADIX.FLEX.GAP.MD}>
                <Box>
                  <CommonTooltip
                    trigger={
                      <MachineProjectorToggleButton
                        className="btn-block"
                        mode="outline"
                      />
                    }
                    text_md="Toggle the ruler and the outline of the machine on the projector (e.g. to verify projector alignment)."
                  />
                </Box>
                <Box>
                  {!env.production && (
                    <CommonTooltip
                      trigger={
                        <MachineProjectorToggleButton
                          className="btn-block"
                          mode="rulers"
                        />
                      }
                      text_md="Toggle the rulers of the machine on the projector (e.g. to verify projector alignment)."
                    />
                  )}
                </Box>
              </Flex>
            </Flex>
          ) : (
            <CommonCallout text="You must be active on the machine to access these controls." />
          )
        }
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
